// @ts-check

/**
 * @file Provides project board filter chips.
 */
import {memo} from "react"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {Pressable, Text, View} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} ProjectBoardFilterChipProps
 * @property {string} filterKey Filter key sent to the parent handler.
 * @property {boolean} hasDot Whether the chip shows the active-change dot.
 * @property {boolean} isActive Whether this chip is active.
 * @property {string} label Chip label.
 * @property {(...args: unknown[]) => unknown} onPress Chip press handler.
 */

/** @typedef {Record<string, never>} ProjectBoardFilterChipState */

/**
 * Renders a selectable project board filter.
 * @augments {ShapeComponent<ProjectBoardFilterChipProps, ProjectBoardFilterChipState>}
 */
class ProjectBoardFilterChip extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  static propTypes = PropTypesExact({
    filterKey: PropTypes.string.isRequired,
    hasDot: PropTypes.bool.isRequired,
    isActive: PropTypes.bool.isRequired,
    label: PropTypes.string.isRequired,
    onPress: PropTypes.func.isRequired
  })

  /** Wires the active theme palette into the filter chip. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the filter chip.
   * @returns {ReactNode} Filter chip content.
   */
  render() {
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    return (
      <Pressable
        onPress={this.tt.onPress}
        style={styles[`chip-${themeName}-${this.p.filterKey}-${this.p.isActive}`] ||= {
          alignItems: "center",
          backgroundColor: this.p.isActive ? colors.chipActiveBackground : colors.chipBackground,
          borderColor: this.p.isActive ? colors.borderStrong : colors.border,
          borderRadius: 999,
          borderWidth: 1,
          flexDirection: "row",
          marginRight: 10,
          minHeight: 36,
          paddingHorizontal: 14,
          paddingVertical: 8
        }}
        testID={`projectBoardScreen/filters/chip/${this.p.filterKey}`}
      >
        <View
          style={styles.chipIcon ||= {
            marginRight: 8,
            width: 14
          }}
          testID={`projectBoardScreen/filters/chip/${this.p.filterKey}/icon`}
        >
          <FontAwesome6
            color={this.p.isActive ? colors.chipActiveText : colors.chipText}
            name="filter"
            size={13}
          />
        </View>
        <Text
          style={styles[`chipText-${themeName}-${this.p.filterKey}-${this.p.isActive}`] ||= {
            color: this.p.isActive ? colors.chipActiveText : colors.chipText,
            fontSize: 13,
            fontWeight: 700
          }}
          testID={`projectBoardScreen/filters/chip/${this.p.filterKey}/label`}
        >
          {this.p.label}
        </Text>
        {this.p.hasDot &&
          <View
            style={styles[`chipDot-${themeName}-${this.p.filterKey}`] ||= {
              backgroundColor: colors.danger,
              borderRadius: 4,
              height: 6,
              marginLeft: 8,
              width: 6
            }}
            testID={`projectBoardScreen/filters/chip/${this.p.filterKey}/dot`}
          />
        }
      </Pressable>
    )
  }

  /** Sends the filter key to the parent press handler. */
  onPress = () => {
    this.p.onPress(this.p.filterKey)
  }
}

/** @type {NamedExoticComponent<ProjectBoardFilterChipProps>} */
const ProjectBoardFilterChipComponent = memo(shapeComponent(ProjectBoardFilterChip))

export default ProjectBoardFilterChipComponent
